import {
  EntityCommonInfoModel,
  FilteredPagedQueryResult,
  FilteredPagedModel,
  CommonMultiDeleteModel,
  CommonEntityActiveSwitch,
} from "../../entities/common";
import { articlesSlice, TagTypes } from "./articlesSlice";
import { getQuerySearchParams } from "../../utils";
import { ArticleDurationTypeModel, ArticleInvoiceCommonInfoModel, ArticleInvoiceModel, ArticleSubscriptionModel } from "../../entities/articles";

export const articleSubscriptionApi = articlesSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ArticleDurationType

    getArticleDurationTypes: builder.query<ArticleDurationTypeModel[], undefined>({
      query: () => "/articleDuration/durationType",
      providesTags: () => [{ type: TagTypes.ArticleDurationType, id: "LIST" }],
    }),
    getArticleDurationTypeCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/articleDuration/durationType/commonInfo",
      providesTags: [{ type: TagTypes.ArticleDurationType, id: "LIST" }],
    }),
    getArticleDurationTypesFilteredPaged: builder.query<FilteredPagedQueryResult<ArticleDurationTypeModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/articleDuration/durationType/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.ArticleDurationType, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.ArticleDurationType,
                id: Number(id),
              })),
            ]
          : [{ type: TagTypes.ArticleDurationType, id: "PARTIAL-LIST" }],
    }),
    getArticleDurationType: builder.query<ArticleDurationTypeModel, number>({
      query: (categoryId) => `/articleDuration/durationType/${categoryId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.ArticleDurationType, id: arg }],
    }),
    addArticleDurationType: builder.mutation({
      query: (initialArticleCategory) => ({
        url: "/articleDuration/durationType",
        method: "POST",
        body: initialArticleCategory,
      }),
      invalidatesTags: [
        { type: TagTypes.ArticleDurationType, id: "LIST" },
        { type: TagTypes.ArticleDurationType, id: "PARTIAL-LIST" },
      ],
    }),
    editArticleDurationType: builder.mutation<ArticleDurationTypeModel, ArticleDurationTypeModel>({
      query: (durationType) => ({
        url: `/articleDuration/durationType/${durationType.id}`,
        method: "PUT",
        body: durationType,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagTypes.ArticleDurationType, id: Number(arg.id) }],
    }),
    switchActiveArticleDurationType: builder.mutation({
      query: (model: CommonEntityActiveSwitch) => ({
        url: `/articleDuration/durationType/switchActive/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.ArticleDurationType, id: arg.id },
        { type: TagTypes.ArticleDurationType, id: "LIST" },
      ],
    }),
    deleteArticleDurationType: builder.mutation({
      query: (durationTypeId: number) => ({
        url: `/articleDuration/durationType/${durationTypeId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.ArticleDurationType, id: arg },
              { type: TagTypes.ArticleDurationType, id: "LIST" },
            ],
    }),
    deleteMultiArticleDurationTypes: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/articleDuration/durationType/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.ArticleDurationType,
                id,
              })),
              { type: TagTypes.ArticleDurationType, id: "LIST" },
            ],
    }),

    // ArticleSubscription

    getArticleSubscriptionCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/articleSubscription/commonInfo",
      providesTags: [{ type: TagTypes.ArticleSubscription, id: "LIST" }],
    }),
    getArticleSubscriptionsFilteredPaged: builder.query<FilteredPagedQueryResult<ArticleSubscriptionModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/articleSubscription/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.ArticleSubscription, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.ArticleSubscription,
                id: Number(id),
              })),
            ]
          : [{ type: TagTypes.ArticleSubscription, id: "PARTIAL-LIST" }],
    }),
    getArticleSubscription: builder.query<ArticleSubscriptionModel, number>({
      query: (articleSubscriptionId) => `/articleSubscription/${articleSubscriptionId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.ArticleSubscription, id: arg }],
    }),
    switchActiveArticleSubscription: builder.mutation({
      query: (model: CommonEntityActiveSwitch) => ({
        url: `/articleSubscription/switchActive/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.ArticleSubscription, id: arg.id },
        { type: TagTypes.ArticleSubscription, id: "LIST" },
      ],
    }),
    deleteArticleSubscription: builder.mutation({
      query: (articleSubscriptionId: number) => ({
        url: `/articleSubscription/${articleSubscriptionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.ArticleSubscription, id: arg },
              { type: TagTypes.ArticleSubscription, id: "LIST" },
            ],
    }),
    deleteMultiArticleSubscriptions: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/articleSubscription/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.ArticleSubscription,
                id,
              })),
              { type: TagTypes.ArticleSubscription, id: "LIST" },
            ],
    }),

    // ArticleInvoice

    getArticleInvoiceCommonInfo: builder.query<ArticleInvoiceCommonInfoModel, undefined>({
      query: () => "/articleInvoice/commonInfo",
      providesTags: [{ type: TagTypes.ArticleInvoice, id: "LIST" }],
    }),
    getArticleInvoicesFilteredPaged: builder.query<FilteredPagedQueryResult<ArticleInvoiceModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/articleInvoice/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.ArticleInvoice, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.ArticleInvoice,
                id: Number(id),
              })),
            ]
          : [{ type: TagTypes.ArticleInvoice, id: "PARTIAL-LIST" }],
    }),
    getArticleInvoice: builder.query<ArticleInvoiceModel, number>({
      query: (invoiceId) => `/articleInvoice/${invoiceId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.ArticleInvoice, id: arg }],
    }),
    deleteArticleInvoice: builder.mutation({
      query: (invoiceId: number) => ({
        url: `/articleInvoice/${invoiceId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.ArticleInvoice, id: arg },
              { type: TagTypes.ArticleInvoice, id: "LIST" },
            ],
    }),
    deleteMultiArticleInvoices: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/articleInvoice/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.ArticleInvoice,
                id,
              })),
              { type: TagTypes.ArticleInvoice, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetArticleDurationTypesQuery,
  useGetArticleDurationTypeCommonInfoQuery,
  useGetArticleDurationTypesFilteredPagedQuery,
  useGetArticleDurationTypeQuery,
  useAddArticleDurationTypeMutation,
  useEditArticleDurationTypeMutation,
  useSwitchActiveArticleDurationTypeMutation,
  useDeleteArticleDurationTypeMutation,
  useDeleteMultiArticleDurationTypesMutation,

  useGetArticleSubscriptionCommonInfoQuery,
  useGetArticleSubscriptionsFilteredPagedQuery,
  useGetArticleSubscriptionQuery,
  useSwitchActiveArticleSubscriptionMutation,
  useDeleteArticleSubscriptionMutation,
  useDeleteMultiArticleSubscriptionsMutation,

  useGetArticleInvoiceCommonInfoQuery,
  useGetArticleInvoicesFilteredPagedQuery,
  useGetArticleInvoiceQuery,
  useDeleteArticleInvoiceMutation,
  useDeleteMultiArticleInvoicesMutation,
} = articleSubscriptionApi;
